import { useEffect, useRef, useState } from 'react';
import { deviceFingerprint } from '../capture/camera';
import { BURST_COUNT, BURST_INTERVAL_MS, BURST_START_DELAY_MS, burstId, nextBurstIndex, runBurst } from '../capture/burst';
import { DEFAULT_THRESHOLDS } from '../capture/gates';
import { buildCapture, centimetresToMm, checkMeta, DISTANCE_CHOICES_M, isCalibrationCode, LABEL_PLACEMENTS, metresToMm, type LabelPlacement, type ShotMeta } from '../capture/meta';
import { guideFor } from '../capture/guide';
import { DEFAULT_ORIENTATION_LIMITS } from '../capture/orientation';
import type { CaptureResult } from '../capture/session';
import { sha256Hex } from '../capture/sha256';
import { useCaptureSession } from '../capture/useCaptureSession';
import { PitchGauge, ScenarioOutline } from '../ui/ScenarioGuide';
import { Button, Card, Field, Hint } from '../ui/Ui';
import { calibrationStep, caseSuffix } from './calibration';
import { jpegSize } from './jpeg';
import { EXPORT_SCHEMA_VERSION, legacyTruthIds, missingCaptureIds, recorderDb, type RecordedCase } from './db';
import { exportName } from './exportName';
import { newestCase, shotSummary } from './shot';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// IL RECORDER (Fase 0, ARCHITETTURA §14): la pagina che produce i bundle di replay. Stessa fotocamera, stessi gate,
// stessa gravità del flusso vero; in più la verità al metro digitata lì per lì. I casi restano in IndexedDB finché non
// si esportano in un JSON (fotogrammi in base64 + hash): `dim-tools bundle unpack` li riscrive byte per byte e
// confronta l'hash (gate "round-trip byte-identical"). Nessuna rete: funziona anche in magazzino senza Wi-Fi.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export function Recorder() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scratchRef = useRef<HTMLCanvasElement>(null);
  // Fotocamera, sensore e ciclo di controllo sono la sessione di cattura condivisa con la schermata operatore (WP1).
  const cap = useCaptureSession(videoRef, scratchRef, { thresholds: DEFAULT_THRESHOLDS, limits: DEFAULT_ORIENTATION_LIMITS });
  const [cases, setCases] = useState<RecordedCase[]>([]);
  const [itemId, setItemId] = useState('');
  const [scenario, setScenario] = useState<'Postazione' | 'Scaffale'>('Postazione');
  // Frame di cattura (ADR-026): il FRONTE è il lato che si vede dalla corsia, la PROFONDITÀ il lato verso il fondo. Nel
  // bundle `truth.frontMm` e `truth.depthMm` hanno gli stessi nomi di Measurement: Replay li confronta campo per campo,
  // quindi un "lungo/corto" scritto qui scambierebbe gli assi e inventerebbe un errore.
  const [front, setFront] = useState('');
  const [depth, setDepth] = useState('');
  const [h, setH] = useState('');
  const [palletType, setPalletType] = useState('EPAL1');
  // Metadati di ripresa (ADR-031): distanza (m) e altezza della pistola (cm) si dichiarano a ogni scatto, etichetta e film una volta per pallet.
  const [distance, setDistance] = useState('');
  const [cameraHeight, setCameraHeight] = useState('');
  const [labelPlacement, setLabelPlacement] = useState<LabelPlacement | null>(null);
  const [wrapped, setWrapped] = useState<boolean | null>(null);
  const meta: ShotMeta = { distanceMm: metresToMm(distance), cameraHeightMm: centimetresToMm(cameraHeight), labelPlacement, wrappedInFilm: wrapped };
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // La raffica per la stabilità del campo visivo (WP4): avanzamento e modo di interromperla.
  const [burst, setBurst] = useState<{ done: number; total: number } | null>(null);
  const burstAbort = useRef<AbortController | null>(null);
  const [panel, setPanel] = useState(false);
  // Qualità dal vivo, posizione della pistola e stabilità: non bloccano lo scatto, lo guidano. Il richiamo di qualità ha la precedenza.
  // Con la scacchiera (CAL…) non valgono: il monitor bianco «brucia» sempre, e le posizioni storte sono proprio quelle che servono.
  const calibrating = isCalibrationCode(itemId);
  const hint = calibrating ? null : (cap.live?.quality ?? cap.live?.orientationHint ?? null);
  const calibration = calibrating ? calibrationStep(cases.filter((c) => c.id.startsWith(itemId.trim() + '-')).length) : null;
  const ring = cap.live?.ring ?? 0;
  // Lo scatto appena fatto, annullabile per qualche secondo con un tocco (errori di codice, foto da rifare).
  const [lastShot, setLastShot] = useState<string | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void recorderDb.all().then(setCases);
    return () => {
      if (undoTimer.current) clearTimeout(undoTimer.current);
    };
  }, []);

  // Il profilo di calibrazione vale per un fuoco fisso: se non si è bloccato, le foto di questa sessione non sono confrontabili.
  useEffect(() => {
    if (cap.focus !== null && cap.focus !== 'locked') setMsg(`Fuoco NON bloccato (${cap.focus}): calibrazione e foto non sono confrontabili`);
  }, [cap.focus]);

  useEffect(() => {
    if (cap.error !== null) setMsg(`Fotocamera: ${cap.error}`);
  }, [cap.error]);

  // Un altro pallet, un'altra etichetta e un altro film: si dichiarano di nuovo (la distanza si rifà a ogni scatto).
  useEffect(() => {
    setLabelPlacement(null);
    setWrapped(null);
  }, [itemId]);

  const toMm = (v: string): number | null => (v.trim() === '' ? null : Math.round(Number(v.replace(',', '.')) * 10));

  /** Un caso registrato dallo scatto: lo stesso per lo scatto normale e per la raffica, così i due non possono divergere nei campi della richiesta. */
  const caseFrom = async (id: string, shot: CaptureResult, truth: RecordedCase['truth']): Promise<RecordedCase> => ({
    id,
    blob: shot.blob,
    sha256: await sha256Hex(shot.blob),
    request: {
      itemId: itemId.trim(),
      accuracyClass: 'Operational',
      scenario,
      gates: { blurVariance: shot.gates.blurVariance, clipLowShare: shot.gates.clipLowShare, clipHighShare: shot.gates.clipHighShare, glareShare: shot.gates.glareShare },
      gravity: shot.gravity,
      deviceFingerprint: shot.device.fingerprint,
      trackSettings: shot.settings,
      // Quanto si muoveva la pistola allo scatto (°/s): serve a spiegare le foto mosse quando si analizzano.
      angularSpeedDegPerS: shot.angularSpeedDegPerS,
    },
    truth,
    capture: isCalibrationCode(itemId) ? null : buildCapture(meta, shot.orientation),
    photo: shot.photo,
    savedAtUtc: new Date().toISOString(),
  });

  /**
   * La raffica: 100 scatti della scacchiera a pistola appoggiata, per `dim-fov-stability`. Il codice (Pallet) è il prefisso (`STAB-001`…); i metadati di ripresa
   * sono gli stessi per tutti (distanza compresa: la pistola non si muove). Parte dopo qualche secondo, perché toccare lo schermo sposta la pistola.
   */
  const startBurst = async (): Promise<void> => {
    const prefix = itemId.trim();
    if (prefix === '') return;
    const problems = checkMeta(meta);
    if (problems.length > 0) {
      setMsg(`Prima della raffica mancano: ${problems.join(', ')}`);
      return;
    }

    const controller = new AbortController();
    burstAbort.current = controller;
    setBusy(true);
    setPanel(false);
    setBurst({ done: 0, total: BURST_COUNT });
    setMsg(`La raffica parte tra ${BURST_START_DELAY_MS / 1000} secondi: appoggia la pistola e non toccarla`);
    try {
      const firstIndex = nextBurstIndex((await recorderDb.all()).map((c) => c.id), prefix);
      const result = await runBurst({
        count: BURST_COUNT,
        intervalMs: BURST_INTERVAL_MS,
        startDelayMs: BURST_START_DELAY_MS,
        firstIndex,
        signal: controller.signal,
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
        shootOne: async (n) => {
          const shot = await cap.shoot();
          await recorderDb.put(await caseFrom(burstId(prefix, n), shot, null));
        },
        onProgress: (done, total) => {
          setBurst({ done, total });
          setMsg(`Raffica ${done}/${total}: non toccare la pistola`);
        },
      });
      setMsg(result.stopped ? `Raffica interrotta a ${result.done} scatti (restano nei casi)` : `Raffica finita: ${result.done} scatti ${burstId(prefix, firstIndex)}…${burstId(prefix, firstIndex + result.done - 1)}`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      burstAbort.current = null;
      setBurst(null);
      setBusy(false);
      setCases(await recorderDb.all());
    }
  };

  const record = async (): Promise<void> => {
    if (itemId.trim() === '') {
      // Senza codice lo scatto non ha un nome: si dice, invece di non fare niente.
      setMsg('Prima scrivi il codice nel campo Pallet (per la scacchiera: CALPAG)');
      setPanel(true);
      return;
    }
    // La campagna è una sola (ADR-031): uno scatto senza distanza, altezza, etichetta e film non entra nei dati. Le viste della scacchiera (CAL…) non hanno un bancale.
    const problems = isCalibrationCode(itemId) ? [] : checkMeta(meta);
    if (problems.length > 0) {
      setMsg(`Prima di scattare mancano: ${problems.join(', ')}`);
      setPanel(true);
      return;
    }
    setBusy(true);
    try {
      const shot = await cap.shoot();
      const n = cases.filter((c) => c.id.startsWith(itemId.trim() + '-')).length;
      const id = `${itemId.trim()}-${caseSuffix(n)}`;
      await recorderDb.put(await caseFrom(id, shot, { frontMm: toMm(front), depthMm: toMm(depth), heightFloorToTopMm: toMm(h), palletType }));
      setDistance(''); // la distanza cambia a ogni foto: si sceglie di nuovo, mai ereditata per distrazione
      setCases(await recorderDb.all());
      setMsg(shotSummary(id, shot.blob.size, shot.widthPx, shot.heightPx, shot.gates, DEFAULT_THRESHOLDS));
      setLastShot(id);
      if (undoTimer.current) clearTimeout(undoTimer.current);
      undoTimer.current = setTimeout(() => setLastShot(null), 10_000);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const exportAll = async (): Promise<void> => {
    // Con un codice di calibrazione si esportano solo le sue viste: le prove vecchie rimaste nel dispositivo non c'entrano e non devono fermare l'export.
    const chosen = isCalibrationCode(itemId) ? cases.filter((c) => c.id.startsWith(itemId.trim() + '-')) : cases;
    const legacy = legacyTruthIds(chosen);
    if (legacy.length > 0) {
      setMsg(`Export fermo: ${legacy.length} casi sono del formato vecchio (${legacy.slice(0, 3).join(', ')}${legacy.length > 3 ? '…' : ''}). Cancellali e rifai le foto`);
      return;
    }

    const noCapture = missingCaptureIds(chosen.filter((c) => !isCalibrationCode(String(c.request.itemId ?? ''))));
    if (noCapture.length > 0) {
      setMsg(`Export fermo: ${noCapture.length} casi non hanno i metadati di ripresa (${noCapture.slice(0, 3).join(', ')}${noCapture.length > 3 ? '…' : ''}). Cancellali e rifai le foto`);
      return;
    }

    const dev = await deviceFingerprint();
    const first = chosen[0];
    // La foto ferma: hash dei suoi byte e dimensioni del raster (senza EXIF), come le ricontrolla `bundle unpack`. Con almeno una foto il formato è il 2.2.
    const photoOf = async (p: Blob | null | undefined) => {
      if (!p) return undefined;
      const size = jpegSize(new Uint8Array(await p.arrayBuffer()));
      return size ? { sha256: await sha256Hex(p), base64: await toBase64(p), widthPx: size.widthPx, heightPx: size.heightPx } : undefined;
    };
    const out = {
      schemaVersion: chosen.some((c) => c.photo) ? '2.2' : EXPORT_SCHEMA_VERSION,
      name: exportName(new Date()),
      device: { model: dev.model, fingerprint: dev.fingerprint, captureWidthPx: Number(cap.settings?.width ?? 0), captureHeightPx: Number(cap.settings?.height ?? 0) },
      cases: await Promise.all(chosen.map(async (c) => ({ id: c.id, sha256: c.sha256, frameBase64: await toBase64(c.blob), request: c.request, truth: c.truth, capture: c.capture, photo: await photoOf(c.photo) }))),
    };
    if (!first) return;
    const blob = new Blob([JSON.stringify(out)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${out.name}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };

  // Annulla il caso registrato per ultimo (per data di salvataggio): un solo caso alla volta, così le lettere (-a, -b, -c) restano senza buchi.
  const removeLast = async (): Promise<void> => {
    const last = newestCase(cases);
    if (!last) return;
    await recorderDb.remove(last.id);
    setCases(await recorderDb.all());
    setLastShot(null);
    setMsg(`${last.id} annullato`);
  };

  // Il pulsante di scatto non scatta senza il codice del pallet: apre il pannello dei dati, dove si scrive.
  const shutter = (): void => {
    if (itemId.trim() === '') setPanel(true);
    else void record();
  };

  return (
    <>
      {/* SCHERMATA DI SCATTO, come una app fotocamera: l'immagine riempie lo schermo. Il video della pistola in verticale è 1200×1600
          (4:3 girato) e lo schermo è 320×405: `object-contain` lo mostra INTERO (ciò che si vede è ciò che finisce nella foto), con
          scarti di pochi pixel ai lati. Una finestra 4:3 con `object-cover` ne mostrava circa il 56 %. */}
      <div className="fixed inset-0 bg-black">
        <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-contain" />
        <canvas ref={scratchRef} className="hidden" />
        {/* La stessa guida della schermata operatore: il disegno dello scenario e la livella dell'inclinazione, sul riquadro 3:4 del video. */}
        <svg viewBox="0 0 300 400" preserveAspectRatio="xMidYMid meet" className="pointer-events-none absolute inset-0 h-full w-full">
          {calibration ? (
            // Con la scacchiera il disegno del bancale non c'entra: c'è solo il riquadro in cui deve cadere per questa foto.
            calibration.area && (
              <rect
                x={calibration.area[0] * 300 + 2}
                y={calibration.area[1] * 400 + 2}
                width={(calibration.area[2] - calibration.area[0]) * 300 - 4}
                height={(calibration.area[3] - calibration.area[1]) * 400 - 4}
                fill="rgba(255,214,10,0.12)"
                stroke="#FFD60A"
                strokeWidth={3}
                rx={6}
              />
            )
          ) : (
            <>
              <ScenarioOutline scenario={scenario} />
              <PitchGauge orientation={cap.live?.orientation ?? null} limits={DEFAULT_ORIENTATION_LIMITS} />
            </>
          )}
        </svg>

        <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent px-3 pb-6 pt-2 text-[13px] text-white">
          <span>Recorder · {cases.length} casi</span>
          <span className="font-mono">{itemId.trim() === '' ? 'codice?' : itemId.trim()}</span>
        </div>
        {!calibration && <p className="pointer-events-none absolute inset-x-0 top-[26px] px-3 text-[11px] text-white/80">{guideFor(scenario).short}</p>}
        {msg && (
          <div className={`absolute inset-x-2 top-9 flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-[13px] text-white ${msg.includes('DA RIFARE') ? 'bg-rosso/85' : 'bg-black/65'}`}>
            <span>{msg}</span>
            {lastShot && (
              <button type="button" onClick={() => void removeLast()} className="min-h-tocco shrink-0 rounded-lg bg-white/30 px-2 text-[13px] font-semibold active:opacity-70">
                Annulla scatto
              </button>
            )}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
          {/* Lo stato dal vivo: i controlli del prodotto sul video e la stabilità dal sensore. Non blocca lo scatto, lo guida. */}
          <p className={`mb-3 rounded-lg px-3 py-1.5 text-[14px] text-white ${hint ? 'bg-arancio/90' : ring >= 1 ? 'bg-verde/90' : 'bg-black/55'}`}>
            {calibration ? calibration.prompt : hint ? hint.hint : ring >= 1 ? 'Fermo: scatta' : 'Tieni ferma la pistola'}
          </p>
          {burst ? (
            // Durante la raffica restano solo i tasti che servono: toccare la pistola la sposta, e l'unico gesto ammesso è interrompere.
            <button type="button" onClick={() => burstAbort.current?.abort()} className="min-h-tocco w-full rounded-xl bg-rosso px-3 text-[16px] font-semibold text-white active:opacity-70">
              Interrompi la raffica ({burst.done}/{burst.total})
            </button>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={() => setPanel(true)} className="min-h-tocco min-w-[64px] rounded-xl bg-white/25 px-3 text-[15px] font-semibold text-white active:opacity-70">
                Dati
              </button>
              <button
                type="button"
                aria-label="Registra"
                disabled={busy}
                onClick={shutter}
                className={`h-16 w-16 rounded-full bg-white ring-4 active:scale-95 disabled:opacity-40 ${hint ? 'ring-arancio' : ring >= 1 ? 'ring-verde' : 'ring-white/40'}`}
              />
              <button
                type="button"
                disabled={cases.length === 0}
                onClick={() => void exportAll()}
                className="min-h-tocco min-w-[64px] rounded-xl bg-white/25 px-3 text-[15px] font-semibold text-white active:opacity-70 disabled:opacity-40"
              >
                Esporta
              </button>
            </div>
          )}
        </div>
      </div>

      {panel && (
        <div className="fixed inset-0 z-20 flex flex-col overflow-y-auto bg-fondo">
          <header className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-sep bg-fondo/95 px-4 text-[17px] font-semibold backdrop-blur">
            <span>Dati del pallet</span>
            <button type="button" onClick={() => setPanel(false)} className="min-h-tocco px-2 text-blu active:opacity-70">
              Fatto
            </button>
          </header>
          <main className="flex flex-col gap-3 p-3">
            <Card>
              <Field label="Pallet" value={itemId} onChange={setItemId} inputMode="text" />
              <label className="flex items-center justify-between py-2">
                <span className="text-[15px] text-sec">Scenario</span>
                <span className="flex gap-2">
                  {(['Postazione', 'Scaffale'] as const).map((sc) => (
                    <button key={sc} type="button" onClick={() => setScenario(sc)} className={`min-h-tocco rounded-lg px-3 text-[15px] ${sc === scenario ? 'bg-blu text-white' : 'bg-riemp text-blu'}`}>
                      {sc}
                    </button>
                  ))}
                </span>
              </label>
              <Field label="Tipo" value={palletType} onChange={setPalletType} inputMode="text" />
            </Card>
            <Card>
              <p className="mb-1 text-[13px] text-ter">Ripresa (a ogni foto la distanza; il resto resta finché non cambi pallet)</p>
              <div className="flex flex-wrap items-center gap-2 py-1">
                {DISTANCE_CHOICES_M.map((d) => (
                  <button key={d} type="button" onClick={() => setDistance(String(d))} className={`min-h-tocco rounded-lg px-3 text-[15px] ${metresToMm(distance) === d * 1000 ? 'bg-blu text-white' : 'bg-riemp text-blu'}`}>
                    {String(d).replace('.', ',')} m
                  </button>
                ))}
              </div>
              <Field label="Distanza" value={distance} onChange={setDistance} unit="m" />
              <Field label="Altezza della pistola" value={cameraHeight} onChange={setCameraHeight} unit="cm" />
              <div className="flex items-center justify-between py-2">
                <span className="text-[15px] text-sec">Etichetta</span>
                <span className="flex gap-2">
                  {LABEL_PLACEMENTS.map((p) => (
                    <button key={p.value} type="button" onClick={() => setLabelPlacement(p.value)} className={`min-h-tocco rounded-lg px-3 text-[15px] ${p.value === labelPlacement ? 'bg-blu text-white' : 'bg-riemp text-blu'}`}>
                      {p.label}
                    </button>
                  ))}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-[15px] text-sec">Film</span>
                <span className="flex gap-2">
                  {([true, false] as const).map((w) => (
                    <button key={String(w)} type="button" onClick={() => setWrapped(w)} className={`min-h-tocco rounded-lg px-3 text-[15px] ${w === wrapped ? 'bg-blu text-white' : 'bg-riemp text-blu'}`}>
                      {w ? 'Sì' : 'No'}
                    </button>
                  ))}
                </span>
              </div>
            </Card>
            <Card>
              <p className="mb-1 text-[13px] text-ter">Stabilità del campo visivo: la scacchiera ferma, la pistola APPOGGIATA</p>
              <p className="mb-2 text-[13px] text-sec">
                Scacchiera inclinata e ferma, pistola su un piano, non toccarla: {BURST_COUNT} scatti con il codice come prefisso (STAB-001…). Compila prima codice e ripresa qui sopra.
              </p>
              {burst ? (
                <Button tone="danger" onClick={() => burstAbort.current?.abort()}>
                  Interrompi ({burst.done}/{burst.total})
                </Button>
              ) : (
                <Button tone="secondary" disabled={busy || itemId.trim() === ''} onClick={() => void startBurst()}>
                  Raffica di {BURST_COUNT} scatti
                </Button>
              )}
            </Card>
            <Card>
              <p className="mb-1 text-[13px] text-ter">Verità al metro (facoltativa, cm): si può lasciare vuota e applicare dopo</p>
              <Field label="Fronte" value={front} onChange={setFront} unit="cm" />
              <Field label="Profondità" value={depth} onChange={setDepth} unit="cm" />
              <Field label="Altezza da terra" value={h} onChange={setH} unit="cm" />
              <p className="mt-1 text-[13px] text-ter">Fronte: il lato che vedi dalla corsia. Profondità: il lato verso il fondo. Altezza: da terra alla cima del carico.</p>
            </Card>
            {msg && <Hint tone="info">{msg}</Hint>}
            <ul className="text-[13px] text-sec">
              {cases.slice(-5).map((c) => (
                <li key={c.id} className="font-mono">
                  {c.id} · {c.sha256.slice(0, 8)}
                </li>
              ))}
            </ul>
            <Button tone="secondary" onClick={() => void removeLast()} disabled={cases.length === 0}>
              {cases.length === 0 ? 'Nessun caso da annullare' : `Annulla l'ultimo caso (${newestCase(cases)?.id})`}
            </Button>
            <Button
              tone="danger"
              onClick={() => {
                if (confirm(`Cancellare ${cases.length} casi registrati?`)) void recorderDb.clear().then(() => setCases([]));
              }}
              disabled={cases.length === 0}
            >
              Svuota i casi registrati
            </Button>
          </main>
        </div>
      )}
    </>
  );
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
